/**
 * During the build process, we populate /build-timestamp.txt with the same value
 * we set process.env.REACT_APP_BUILD_TIMESTAMP to.
 *
 * The frontend fetches this file periodically, and if the value in this file
 * differs from process.env.REACT_APP_BUILD_TIMESTAMP, then we know that the
 * code has changed and we should ask the user to reload the page to get the newest code.
 */
import { useSnackbar } from "notistack";
import { useEffect, useState } from "react";

import { Button } from "@material-ui/core";

const CHECK_INTERVAL = 60 * 1000;

export default function useBuildTimestampRefresh(): void {
  const snackbar = useSnackbar();
  const [snackbarIsShown, setSnackbarIsShown] = useState(false);

  useEffect(() => {
    if (!process.env.REACT_APP_BUILD_TIMESTAMP) {
      console.warn(
        "Not using build timestamp system; no REACT_APP_BUILD_TIMESTAMP defined"
      );
      return;
    }

    if (snackbarIsShown) {
      return;
    }

    const buildTimestamp = Number(process.env.REACT_APP_BUILD_TIMESTAMP);
    console.log(`Build timestamp: ${buildTimestamp}`);

    const intervalHandle = setInterval(async () => {
      try {
        const r = await fetch(`/build-timestamp.txt?t=${Date.now()}`);
        if (r.status !== 200) {
          throw new Error("Error fetching build timestamp");
        }

        const tsString = await r.text();

        if (Number(tsString) !== buildTimestamp) {
          setSnackbarIsShown(true);

          // notify the user
          snackbar.enqueueSnackbar(
            "A new version of the Dialer has been released. Please refresh the page to get the latest version.",
            {
              variant: "warning",
              persist: true,
              action: (
                <Button onClick={() => window.location.reload()}>
                  Refresh
                </Button>
              )
            }
          );
        }
      } catch (err: any) {
        console.error(err);
      }
    }, CHECK_INTERVAL);

    return () => clearInterval(intervalHandle);
  }, [snackbarIsShown, snackbar]);
}
